import { useEffect, useState } from "react";
import { fetchToday, send, type TodayData } from "../api.js";
import { Requirements } from "./Requirements.js";
import { dayCT } from "../time.js";

/**
 * Where the plan gets changed.
 *
 * Three bands, in the order a day actually runs: what happened overnight, the
 * two things you do about it, and then the plan itself — which takes the
 * majority of the screen because it is the thing being built.
 *
 * The plan is one draft named for the date, and everything lands in it: the
 * morning file's date changes, somebody off sick, and anything typed into the
 * box at the bottom. It is the same requirement list the Schedule screen uses,
 * against the same scenario machinery, so a change added here behaves
 * identically — enforced on every solve, undone by a toggle, and named by an
 * infeasible core.
 *
 * The draft is not created until something is asked of it. Opening this screen
 * each morning should not mint a scenario and copy 166 assignments for nothing.
 */
export function Today({ go, openScenario, fixOverEstimate }: {
  go: (view: string) => void;
  openScenario: (scenarioId: string) => void;
  /** Opens the Backlog on its over-estimate view. */
  fixOverEstimate?: () => void;
}) {
  const [data, setData] = useState<TodayData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [away, setAway] = useState<{ resourceId: string; from: string; to: string } | null>(null);

  const load = async () => {
    try { setData(await fetchToday()); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const act = async (label: string, fn: () => Promise<any>) => {
    setBusy(label); setErr(null); setNote(null);
    try {
      const r = await fn();
      await load();
      if (r?.churn !== undefined) {
        setNote(r.churn
          ? `Re-planned — ${r.churn} step${r.churn === 1 ? "" : "s"} changed hands.`
          : "Re-planned — nothing had to move.");
      }
      return r;
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (err && !data) return <div className="err">{err}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const isAdmin = data.user.role === "admin";
  // POST /today/away is resource.edit — a manager holds that for their own
  // team, same as "somebody is off" on the People screen. /today/plan and
  // /today/publish are schedule.publish, admin-only, so those two stay
  // admin-gated. TodayData.people carries no teamId, so the away picker
  // cannot be scoped per-row without another fetch — left permissive, and
  // the server's sentence explains a refusal if a manager picks someone
  // outside their team.
  const canRecordAway = isAdmin || data.user.role === "manager";
  const plan = data.dayScenario;
  const enforced = plan?.requirements.filter((r) => r.enabled).length ?? 0;

  // Only what needs attention. A screen that always shows six headings stops
  // being read, so a quiet morning shows one line saying it is quiet.
  const notices: React.ReactNode[] = [];

  if (!data.lastImport) {
    notices.push(
      <Notice key="noimport" tone="wait"
        what="No file imported today"
        why={data.lastImportAny
          ? `The last one was ${dayCT(data.lastImportAny.uploadedAt)}.`
          : "No file has ever been imported."}
        action="Import it" onAct={() => go("import")} />,
    );
  }

  if (data.outToday.length) {
    notices.push(
      <Notice key="outtoday" tone="wait"
        what={`Out today: ${data.outToday.length}`}
        why={
          <>
            {data.outToday.map((o) => (
              <span key={o.resourceId} style={{ marginRight: 10 }}>
                <b>{o.name}</b>{o.halfDay ? <span className="muted"> (half day)</span> : null}
              </span>
            ))}
          </>
        }
        action="People" onAct={() => go("resources")} />,
    );
  }

  if (data.away.length) {
    notices.push(
      <Notice key="away" tone="wait"
        what={`${data.away.length} ${data.away.length === 1 ? "person is" : "people are"} away in the next two weeks`}
        why={
          <>
            {data.away.slice(0, 3).map((a) => (
              <span key={a.resourceId} style={{ marginRight: 10 }}>
                <b>{a.name}</b>{" "}
                <span className="mono muted">
                  {a.days[0]}{a.days.length > 1 ? `…${a.days[a.days.length - 1]}` : ""} ({a.days.length}d)
                </span>
              </span>
            ))}
            {data.away.length > 3 && <span className="muted">and {data.away.length - 3} more</span>}
          </>
        }
        action="People" onAct={() => go("resources")} />,
    );
  }

  if (data.overEstimate > 0) {
    notices.push(
      <Notice key="overestimate" tone="act"
        what={`${data.overEstimate} step${data.overEstimate === 1 ? " is" : "s are"} at or over ${data.overEstimate === 1 ? "its" : "their"} estimate`}
        why="They are still open, so they have dropped out of the load. Re-estimate each one, or complete it."
        action="Review" onAct={() => (fixOverEstimate ? fixOverEstimate() : go("backlog"))} />,
    );
  }

  if (data.needsBreakdown.length) {
    notices.push(
      <Notice key="breakdown" tone="act"
        what={`${data.needsBreakdown.length} project${data.needsBreakdown.length === 1 ? "" : "s"} ${data.needsBreakdown.length === 1 ? "has" : "have"} no breakdown`}
        why={
          <>
            Nothing can be scheduled against them.{" "}
            {data.needsBreakdown.slice(0, 4).map((p) => (
              <span key={p.projectId} className="mono" style={{ marginRight: 8 }}>
                {p.projectId}{p.productCode ? ` (${p.productCode})` : ""}
              </span>
            ))}
            {data.needsBreakdown.length > 4 && (
              <span className="muted">and {data.needsBreakdown.length - 4} more</span>
            )}
          </>
        }
        action="Build them" onAct={() => go("projects")} />,
    );
  }

  if (data.atRisk.length) {
    const worst = data.atRisk[0]!;
    notices.push(
      <Notice key="risk" tone="crit"
        what={`${data.atRisk.length} project${data.atRisk.length === 1 ? "" : "s"} ${data.atRisk.length === 1 ? "finishes" : "finish"} late`}
        why={
          <>
            Worst is <span className="mono">{worst.projectId}</span>: due {worst.dueDate}, the
            plan finishes it {worst.projectedFinish}.
          </>
        }
        action="Delivery" onAct={() => go("project")} />,
    );
  }

  if (data.missingFromFile.length) {
    notices.push(
      <Notice key="missing" tone="wait"
        what={`${data.missingFromFile.length} project${data.missingFromFile.length === 1 ? "" : "s"} no longer in the file`}
        why="Their steps and assignments are untouched."
        action="Review" onAct={() => go("projects")} />,
    );
  }

  return (
    <div className="today-page">
      {/* 1 — what happened overnight. */}
      <div className="notices">
        {notices.length ? notices : (
          <div className="notice quiet">
            Nothing needs attention. The file is in, every project has a breakdown, no promise
            is being missed, nothing is over its estimate and nobody is away.
          </div>
        )}
      </div>

      {err && <div className="err">{err}</div>}
      {note && <div className="hint" style={{ margin: "0 0 10px" }}>{note}</div>}

      {/* 2 — the shortcuts. */}
      {canRecordAway && (
        <div className="day-actions">
          <button className="btn" disabled={!!busy}
            onClick={() => setAway({
              resourceId: data.people[0]?.resourceId ?? "",
              from: data.today, to: data.today,
            })}>
            Somebody is off
          </button>
          {isAdmin && (
            <button className="btn" disabled={!!busy}
              onClick={() => void act("plan", () => send("/today/plan", "POST"))}>
              {busy === "plan" ? "Re-planning…" : "Re-plan now"}
            </button>
          )}
          {plan && (
            <button className="btn" disabled={!!busy} onClick={() => openScenario(plan.scenarioId)}>
              See it on the board
            </button>
          )}
          <span className="spacer" />
          {isAdmin && plan && (
            <button className="btn on" disabled={!!busy}
              onClick={() => void act("publish", async () => {
                if (!confirm(
                  `Publish today's plan? ${enforced} change${enforced === 1 ? "" : "s"} become what the shop is working from.`,
                )) return;
                return send("/today/publish", "POST", { scenarioId: plan.scenarioId });
              })}>
              Publish today's plan
            </button>
          )}
        </div>
      )}

      {/* 3 — the plan, which is what the screen is for. */}
      <div className="day-plan">
        <div className="day-plan-head">
          {/* The date lives in the header rather than in a caption above the
              panel: a floating caption had nothing holding it off the box. */}
          <h3>Today's plan</h3>
          <span className="mono muted">{data.today}</span>
          <span className="chip">{enforced} change{enforced === 1 ? "" : "s"}</span>
          <span className="spacer" />
          <span className="muted">
            {plan
              ? "not published — the shop is still working from the live schedule"
              : "nothing has changed the plan today"}
          </span>
        </div>

        <Requirements
          bare
          scenarioId={plan?.scenarioId ?? null}
          role={data.user.role}
          refreshToken={plan?.requirements.length ?? 0}
          emptyText={
            "Nothing has changed the plan today. Describe a change below — “move 221890 ahead of " +
            "221824”, “keep Eric under 80%”, “finish 218023 by the 5th” — and it joins the list, " +
            "stays enforced as you add more, and can be switched off again."
          }
          resolveScenarioId={async () => {
            const r = await send<{ scenarioId?: string }>("/today/plan", "POST");
            await load();
            return r.scenarioId ?? null;
          }}
          onSolved={(n) => { void load(); if (n) setNote(n); }}
        />
      </div>

      {away && (
        <div className="modal">
          <div className="modal-card">
            <h3>Somebody is off</h3>
            <p className="muted" style={{ fontSize: 13 }}>
              Their days come out of the calendar and today's plan is re-solved around it.
              Weekends are skipped. It joins the plan below as a change you can switch off.
            </p>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label>who
                <select value={away.resourceId}
                  onChange={(e) => setAway({ ...away, resourceId: e.target.value })}>
                  {data.people.map((p) => (
                    <option key={p.resourceId} value={p.resourceId}>{p.name}</option>
                  ))}
                </select>
              </label>
              <label>from
                <input type="date" value={away.from}
                  onChange={(e) => setAway({ ...away, from: e.target.value })} />
              </label>
              <label>to
                <input type="date" value={away.to}
                  onChange={(e) => setAway({ ...away, to: e.target.value })} />
              </label>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn on" disabled={!!busy || !away.resourceId}
                onClick={() => void act("away", async () => {
                  const r = await send("/today/away", "POST", away);
                  setAway(null);
                  return r;
                })}>
                {busy === "away" ? "Re-planning…" : "Record it and re-plan"}
              </button>
              <span className="spacer" />
              <button className="btn" onClick={() => setAway(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One thing that happened overnight, and where to go about it. */
function Notice({ tone, what, why, action, onAct }: {
  tone: "act" | "crit" | "wait";
  what: React.ReactNode;
  why: React.ReactNode;
  action: string;
  onAct: () => void;
}) {
  return (
    <div className={`notice ${tone}`}>
      <div className="notice-body">
        <span className="notice-what">{what}</span>
        <span className="notice-why">{why}</span>
      </div>
      <button className="btn" onClick={onAct}>{action}</button>
    </div>
  );
}
